import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import PicSelect from "@/components/common/PicSelect";
import LinesEditor, { cleanLines } from "@/components/common/LinesEditor";
import { DateField, Field, inputCls } from "@/components/common/Field";
import { PrimaryBtn } from "@/components/common/ui";
import { ProductIcon } from "@/components/bakery/Art";
import { api, errMsg } from "@/lib/api";
import { CATEGORY_ORDER, todayJkt } from "@/lib/format";
import { cn } from "@/lib/utils";

function useCommon() {
  const [date, setDate] = useState(todayJkt());
  const [pic, setPic] = useState("");
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  return { date, setDate, pic, setPic, reason, setReason, notes, setNotes };
}

function CommonFields({ c, prefix, reasonPh }) {
  return (
    <div className="grid gap-3 md:grid-cols-3">
      <DateField value={c.date} onChange={c.setDate} testid={`${prefix}-date`} />
      <PicSelect value={c.pic} onChange={c.setPic} testid={`${prefix}-pic`} />
      <Field label="Alasan" required><input value={c.reason} onChange={(e) => c.setReason(e.target.value)} placeholder={reasonPh} className={inputCls} data-testid={`${prefix}-reason`} /></Field>
    </div>
  );
}

export function OpnameForm({ products, onSaved }) {
  const c = useCommon();
  const [cat, setCat] = useState("Roti");
  const [sys, setSys] = useState({});
  const [phys, setPhys] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/adjustment/system-stock", { params: { date: c.date } }).then((r) => setSys(r.data)).catch(() => {}); }, [c.date]);
  const list = useMemo(() => (products || []).filter((p) => p.active && !p.is_bundle && p.category === cat), [products, cat]);
  const filled = Object.entries(phys).filter(([, v]) => v !== "" && v !== undefined);
  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/adjustment", { date: c.date, mode: "opname", reason: c.reason, pic_user_id: c.pic, notes: c.notes,
        lines: filled.map(([product_id, v]) => ({ product_id, physical_qty: Number(v) })) });
      toast.success(`Stock opname tersimpan · ${data.txn_no}`);
      setPhys({}); onSaved();
      api.get("/adjustment/system-stock", { params: { date: c.date } }).then((r) => setSys(r.data));
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  return (
    <div className="space-y-5" data-testid="opname-form">
      <CommonFields c={c} prefix="opname" reasonPh="mis. Stock opname pagi" />
      <div className="no-scrollbar flex gap-1.5 overflow-x-auto">
        {CATEGORY_ORDER.filter((x) => x !== "Supplier").map((x) => (
          <button key={x} onClick={() => setCat(x)} data-testid={`opname-cat-${x.replace(/\s/g, "-").toLowerCase()}`}
            className={cn("press shrink-0 rounded-md border px-3 py-2 text-xs font-semibold", cat === x ? "border-ink bg-ink text-white" : "border-zinc-300 bg-white")}>{x}</button>
        ))}
      </div>
      <div className="overflow-x-auto rounded-xl border-2 border-ink bg-white">
        <table className="dense-table w-full">
          <thead><tr><th>Produk</th><th className="text-right">Stok Sistem</th><th className="text-center">Stok Fisik</th><th className="text-right">Selisih</th></tr></thead>
          <tbody>
            {list.map((p) => {
              const s = sys[p.id] || 0;
              const v = phys[p.id];
              const diff = v === undefined || v === "" ? null : Number(v) - s;
              return (
                <tr key={p.id} data-testid={`opname-row-${p.code}`}>
                  <td className="whitespace-normal"><span className="flex items-center gap-2"><ProductIcon category={p.category} className="h-4 w-4" />{p.name}</span></td>
                  <td className={cn("num text-right", s < 0 && "font-bold text-brand")}>{s} <span className="text-zinc-400">{p.stock_unit}</span></td>
                  <td className="text-center"><input type="number" min="0" inputMode="numeric" value={v ?? ""} onChange={(e) => setPhys({ ...phys, [p.id]: e.target.value })}
                    className="num h-10 w-24 rounded-md border border-zinc-300 text-center font-bold focus:border-ink focus:outline-none" data-testid={`opname-physical-${p.code}`} /></td>
                  <td className={cn("num text-right font-bold", diff < 0 && "text-brand", diff === 0 && "text-zinc-400")} data-testid={`opname-diff-${p.code}`}>{diff === null ? "–" : diff > 0 ? `+${diff}` : diff}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Field label="Catatan"><Textarea value={c.notes} onChange={(e) => c.setNotes(e.target.value)} data-testid="opname-notes" /></Field>
      <PrimaryBtn onClick={submit} disabled={saving || !filled.length || c.reason.trim().length < 3 || !c.pic} data-testid="opname-submit">
        {saving ? "Menyimpan…" : `Posting Opname (${filled.length} produk)`}
      </PrimaryBtn>
    </div>
  );
}

export function ManualAdjForm({ products, onSaved }) {
  const c = useCommon();
  const [lines, setLines] = useState([{ product_id: "", qty: -1 }]);
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    const ls = cleanLines(lines).map(({ product_id, qty }) => ({ product_id, qty }));
    if (!ls.length) return toast.error("Isi minimal 1 produk dengan qty ≠ 0");
    setSaving(true);
    try {
      const { data } = await api.post("/adjustment", { date: c.date, mode: "manual", reason: c.reason, pic_user_id: c.pic, notes: c.notes, lines: ls });
      toast.success(`Penyesuaian tersimpan · ${data.txn_no}`);
      setLines([{ product_id: "", qty: -1 }]); c.setReason(""); onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  return (
    <div className="space-y-5" data-testid="manual-adj-form">
      <CommonFields c={c} prefix="adj" reasonPh="mis. Salah input kemarin" />
      <Field label="Produk & qty (± dalam satuan stok)" required>
        <LinesEditor lines={lines} setLines={setLines} products={products} allowNegative filter={(p) => !p.is_bundle} testid="adj-lines"
          unitHint={(p, q) => `${q > 0 ? "+" : ""}${q} ${p.stock_unit} · stok sekarang ${p.stock}`} />
      </Field>
      <Field label="Catatan"><Textarea value={c.notes} onChange={(e) => c.setNotes(e.target.value)} data-testid="adj-notes" /></Field>
      <PrimaryBtn onClick={submit} disabled={saving || c.reason.trim().length < 3 || !c.pic} data-testid="adj-submit">{saving ? "Menyimpan…" : "Simpan Penyesuaian"}</PrimaryBtn>
    </div>
  );
}
